import { Control } from '@trustero/trustero-api-web/lib/model/control_pb'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { Framework } from '@trustero/trustero-api-web/lib/model/framework_pb'
import { NONE_ID } from 'src/Utils/globalConstants'
import { Owner } from 'src/Utils/globalEnums'
import { DropdownItemType } from '../IndexPage/FilterBar/FilterDropdowns/FilterDropdown.constants'

// The picker's filters, kept in the modal's own state. Labels hold ids (or NONE_ID for
// "none"); frameworks hold framework ids, NONE_ID and requirementValue()s; "More
// filters" holds namespaced values (MORE_FILTER_KEYS) so one dropdown can carry several
// filters.
export type ControlPickerFilters = {
  labelIds: string[]
  frameworkIds: string[]
  more: string[]
}

export const NO_CONTROL_PICKER_FILTERS: ControlPickerFilters = {
  labelIds: [],
  frameworkIds: [],
  more: [],
}

export const hasControlPickerFilters = (
  filters: ControlPickerFilters,
): boolean =>
  filters.labelIds.length > 0 ||
  filters.frameworkIds.length > 0 ||
  filters.more.length > 0

export enum MORE_FILTER_KEYS {
  STATUS = 'status',
  POLICY = 'policy',
  DEPARTMENT = 'department',
  OWNER = 'owner',
  RISK = 'risk',
}

const MORE_FILTER_NAMES: Record<MORE_FILTER_KEYS, string> = {
  [MORE_FILTER_KEYS.STATUS]: 'Status',
  [MORE_FILTER_KEYS.POLICY]: 'Policy',
  [MORE_FILTER_KEYS.DEPARTMENT]: 'Department',
  [MORE_FILTER_KEYS.OWNER]: 'Owner',
  [MORE_FILTER_KEYS.RISK]: 'Associated Risk',
}

const moreValue = (key: MORE_FILTER_KEYS, value: string): string =>
  `${key}:${value}`

// Each control's values for the "More filters" groups. Status is the name the control
// shows, so a custom status filters by its own name.
const moreFilterValues = (
  control: Control,
  statusName: string,
): Record<MORE_FILTER_KEYS, { value: string; label: string }[]> => ({
  [MORE_FILTER_KEYS.STATUS]: [{ value: statusName, label: statusName }],
  [MORE_FILTER_KEYS.POLICY]: control.getPolicyModelId()
    ? [
        {
          value: control.getPolicyModelId(),
          label: control.getPolicyName() || control.getPolicyModelId(),
        },
      ]
    : [{ value: NONE_ID, label: 'No Policy' }],
  [MORE_FILTER_KEYS.DEPARTMENT]: control.getDepartmentId()
    ? [
        {
          value: control.getDepartmentId(),
          label: control.getDepartmentShortName() || control.getDepartmentId(),
        },
      ]
    : [{ value: NONE_ID, label: 'No Department' }],
  [MORE_FILTER_KEYS.OWNER]: control.getOwnerEmail()
    ? [{ value: control.getOwnerEmail(), label: control.getOwnerEmail() }]
    : [{ value: NONE_ID, label: Owner.UNASSIGNED }],
  [MORE_FILTER_KEYS.RISK]: control.getLinkedRisksList().map((risk) => ({
    value: risk.getId(),
    label: `${risk.getCustomId()} ${risk.getName()}`.trim(),
  })),
})

// "More filters" options built from the controls on offer, so every option matches at
// least one row and no extra lookups are needed.
export const buildMoreFilterOptions = (
  controls: Control[],
  statusName: (control: Control) => string,
): DropdownItemType[] => {
  const groups = new Map<MORE_FILTER_KEYS, Map<string, string>>()
  controls.forEach((control) => {
    const values = moreFilterValues(control, statusName(control))
    ;(Object.keys(values) as MORE_FILTER_KEYS[]).forEach((key) => {
      const group = groups.get(key) ?? new Map<string, string>()
      values[key].forEach(({ value, label }) => group.set(value, label))
      groups.set(key, group)
    })
  })
  return (Object.values(MORE_FILTER_KEYS) as MORE_FILTER_KEYS[])
    .filter((key) => (groups.get(key)?.size ?? 0) > 0)
    .map((key) => {
      const options = Array.from(groups.get(key) ?? [])
        // "None" options first, then by name.
        .sort(([aValue, aLabel], [bValue, bLabel]) =>
          aValue === NONE_ID
            ? -1
            : bValue === NONE_ID
            ? 1
            : aLabel.localeCompare(bLabel),
        )
        .map(([value, label]) => ({ value: moreValue(key, value), label }))
      return {
        value: key,
        label: MORE_FILTER_NAMES[key],
        nestedFilter: { filterParam: key, filterValues: options },
      }
    })
}

// A requirement's value in the Frameworks filter, kept apart from framework ids.
export const requirementValue = (requirementModelId: string): string =>
  `requirement:${requirementModelId}`

// The account's frameworks that at least one control on offer is in, plus "None". Each
// framework lists its requirements under it, as the index pages' Framework filter does.
export const buildFrameworkFilterOptions = (
  controls: Control[],
  frameworks: ComplianceFramework[],
  requirements: Framework[],
): DropdownItemType[] => {
  const used = new Set(
    controls.flatMap((control) => control.getComplianceFrameworkIdsList()),
  )
  const requirementsByFramework = new Map<string, DropdownItemType[]>()
  requirements.forEach((requirement) => {
    const frameworkId = requirement.getComplianceFrameworkId()
    requirementsByFramework.set(frameworkId, [
      ...(requirementsByFramework.get(frameworkId) ?? []),
      {
        value: requirementValue(requirement.getModelId()),
        label: requirement.getName(),
      },
    ])
  })
  return [
    { value: NONE_ID, label: 'None' },
    ...frameworks
      .filter((framework) => used.has(framework.getId()))
      .map((framework) => {
        const frameworkRequirements = requirementsByFramework.get(
          framework.getId(),
        )
        return {
          value: framework.getId(),
          label: framework.getName(),
          nestedFilter: frameworkRequirements?.length
            ? {
                filterParam: framework.getId(),
                filterValues: frameworkRequirements,
              }
            : undefined,
        }
      }),
  ]
}

const matchesAny = (values: string[], selected: Set<string>): boolean =>
  values.some((value) => selected.has(value))

// Whether a control passes the filters: every filter in use must match (AND), and a
// filter matches when the control has any of its selected values (OR).
export const controlMatchesPickerFilters = (
  control: Control,
  filters: ControlPickerFilters,
  statusName: (control: Control) => string,
): boolean => {
  if (filters.labelIds.length > 0) {
    const labels = control.getLabelIdsList()
    const selected = new Set(filters.labelIds)
    if (!matchesAny(labels.length ? labels : [NONE_ID], selected)) {
      return false
    }
  }
  if (filters.frameworkIds.length > 0) {
    // A control matches a selected framework it is in, or a selected requirement it
    // is mapped to (framework_ids holds requirement model ids).
    const frameworkIds = control.getComplianceFrameworkIdsList()
    const selected = new Set(filters.frameworkIds)
    const values = [
      ...(frameworkIds.length ? frameworkIds : [NONE_ID]),
      ...control.getFrameworkIdsList().map(requirementValue),
    ]
    if (!matchesAny(values, selected)) {
      return false
    }
  }
  if (filters.more.length > 0) {
    const selected = new Set(filters.more)
    const values = moreFilterValues(control, statusName(control))
    for (const key of Object.values(MORE_FILTER_KEYS) as MORE_FILTER_KEYS[]) {
      const inUse = filters.more.some((value) => value.startsWith(`${key}:`))
      if (
        inUse &&
        !matchesAny(
          values[key].map(({ value }) => moreValue(key, value)),
          selected,
        )
      ) {
        return false
      }
    }
  }
  return true
}
